import { httpsCallable } from "firebase/functions";
import { isPlainObject, toLower } from "lodash";
import { auth, functions } from "../firebase";
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberNotEmpty, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { ACCOUNT_CONSENT_SOURCES, STAFF_CREATED_CONSENT_SOURCE } from "../utils/marketingConsentUtils";
import { getDeviceAccountTimeZone } from "../utils/timeZoneUtils";
import User from "./User";
import { toE164PhoneNumber } from "../utils/stringFormatter";
import Address from "./Address";
import AuthService from "../firebase/AuthService";
import { sendWelcomeEmail } from "../firebase/Email";
import CoveredEntityUser from "./CoveredEntityUser";
import { getFirebaseErrorCode } from "../utils/errorUtils";
import { ROLE_FLAGS, toRoleFlags } from "../utils/roleFlagUtils";
import AffiliateUser from "./AffiliateUser";
import PartnerUser from "./PartnerUser";

// deliberately short next to the 6000000ms every other callable on this class uses. account creation is
// the customer's first blocking wait on the funnel, and an effectively unbounded one strands them
// MUST stay above `createfunnelaccount`'s own `timeoutSeconds` (20s). The client giving up while the
// server runs on is worse here than anywhere else in the funnel: this call creates the auth user and the
// users document, `pendingCreatedAccountRef` is set only AFTER it returns, and the funnel's outer catch
// renders "A Patient exists in our system with the same Email Address" - about the account it just made
// for them. A resubmit then hits auth/email-already-exists and says the same thing again.
const CREATE_FUNNEL_ACCOUNT_TIMEOUT = 30000;
// MUST stay above `redeemresumelink`'s `timeoutSeconds` (20s) for the same reason
const REDEEM_RESUME_LINK_TIMEOUT = 30000;

// an entity add form stops before the entity record and the welcome email when the new login did not take its role, so
// submitting again - and confirming the account it now finds - finishes both through the existing-account branch
const NEW_ACCOUNT_ROLE_NOT_APPLIED_MESSAGE = 'The account was created, but its permissions could not be applied to the login, so nothing else was added. Submit the form again and confirm the account to finish.';
const EXISTING_ACCOUNT_ROLE_NOT_ADDED_MESSAGE = 'The role could not be added to the existing account. Please try again.';
// an add form looks the email up and asks staff to confirm an existing account; one that appears after that check is
// refused rather than given a role unconfirmed
const EXISTING_ACCOUNT_UNCONFIRMED_MESSAGE = 'An account already exists for this email address. Submit again to review it before any roles are added.';

const isDuplicatePhoneCheckBypassNumber = (phoneNumber) => {
    return ['+13108781003'].includes(toE164PhoneNumber(phoneNumber));
};

class AuthUser {
    constructor() { }

    static isDuplicatePhoneCheckBypassNumber = isDuplicatePhoneCheckBypassNumber;

    static getByUserId = async (userId) => {
        const func = httpsCallable(functions, "getauthuser", { timeout: 6000000 });
        const res = await func({ userId: userId });
        return res.data;
    }

    static getByEmail = async (email) => {
        const func = httpsCallable(functions, "getauthuser", { timeout: 6000000 });
        const res = await func({ email: email });
        return res.data;
    }

    static getByPhoneNumber = async (phoneNumber) => {
        if (isDuplicatePhoneCheckBypassNumber(phoneNumber)) {
            return null;
        }

        const func = httpsCallable(functions, 'getauthuser', { timeout: 6000000 });
        const res = await func({ phone: phoneNumber, phoneNumber: phoneNumber });
        return res.data;
    }

    static getAll = async () => {
        const func = httpsCallable(functions, "getallauthusers", { timeout: 6000000 });
        const listAuthUsersRes = await func({ maxResults: 10 });

        return listAuthUsersRes.data.users;
    }

    static existsByEmail = async (email) => {
        const func = httpsCallable(functions, "doesauthuserexist", { timeout: 6000000 });
        const res = await func({ email: email });
        return res.data;
    }

    static existsByPhoneNumber = async (phoneNumber) => {
        if (isDuplicatePhoneCheckBypassNumber(phoneNumber)) {
            return false;
        }

        const func = httpsCallable(functions, 'doesauthuserexist', { timeout: 6000000 });
        const res = await func({ phone: phoneNumber, phoneNumber: phoneNumber });
        return res.data;
    }

    static existsUserByPhoneNumber = async (phoneNumber, userId = null) => {
        if (isDuplicatePhoneCheckBypassNumber(phoneNumber)) {
            return false;
        }

        const func = httpsCallable(functions, 'doesuserphoneexist', { timeout: 6000000 });
        const res = await func({ phone: phoneNumber, phoneNumber: phoneNumber, userId: userId });
        return res.data;
    }

    static getEmailByPhoneNumber = async (phoneNumber) => {
        const func = httpsCallable(functions, 'getauthuseremail', { timeout: 6000000 });
        const res = await func({ phone: phoneNumber, phoneNumber: phoneNumber });
        return res.data;
    }

    // exchanges an abandoned-cart resume token for a custom token; the server answers every refusal
    // with one generic message, so the caller learns only that the link did not work
    static redeemResumeLink = async (token) => {
        const func = httpsCallable(functions, 'redeemresumelink', { timeout: REDEEM_RESUME_LINK_TIMEOUT });
        const res = await func({ token: token });
        return res.data;
    }

    static getUserIdByEmail = async (email) => {
        const func = httpsCallable(functions, "getauthuserid", { timeout: 6000000 });
        const res = await func({ email: email });
        return res.data;
    }

    static getUserIdByPhoneNumber = async (phoneNumber) => {
        const func = httpsCallable(functions, 'getauthuserid', { timeout: 6000000 });
        const res = await func({ phone: phoneNumber, phoneNumber: phoneNumber });
        return res.data;
    }

    static getClaims = async (userId) => {
        const func = httpsCallable(functions, "getauthuserclaims", { timeout: 6000000 });
        const res = await func({ userId: userId });
        return res.data;
    }

    static setClaims = async (data) => {
        if (isObjectNotEmpty(data) && isStringNotEmpty(data.userId)) {
            const func = httpsCallable(functions, "setauthuserclaims", { timeout: 6000000 });
            const res = await func(data);

            return isObjectNotEmpty(res) && isBooleanTrue(res.data);
        }

        return false;
    }

    // the roles arrive named, so no flag can be read as another role's. every role in ROLE_FLAGS is sent and one the
    // object leaves out is sent as false and revoked; anything but an object of flags is refused, since reading it as
    // no roles at all would revoke every one the login holds
    static createRoleClaims = async (userId, roles, phoneVerified = null) => {
        if (!isPlainObject(roles)) {
            throw new Error('createRoleClaims takes the roles as one object of named flags.');
        }

        const claims = { userId: userId, ...toRoleFlags(roles) };
        if (isNotNullOrUndefined(phoneVerified)) { claims.phoneVerified = isBooleanTrue(phoneVerified); }

        return await this.setClaims(claims);
    }

    static setCoveredEntityClaims = async (data) => {
        if (isObjectNotEmpty(data) && isStringNotEmpty(data.userId)) {
            const func = httpsCallable(functions, "setauthusercoveredentityclaims", { timeout: 6000000 });
            const res = await func(data);

            return isObjectNotEmpty(res) && isBooleanTrue(res.data);
        }

        return false;
    }

    static updateCoveredEntityClaims = async (coveredEntityId, userId, roles = []) => {
        if (isStringNotEmpty(coveredEntityId) && isStringNotEmpty(userId)) {
            let data = { userId: userId };

            const existingClaims = await this.getClaims(userId);

            let updatedCoveredEntities = {};
            if (isObjectNotEmpty(existingClaims) && isObjectNotEmpty(existingClaims.coveredEntities)) {
                updatedCoveredEntities = { ...existingClaims.coveredEntities };
            }

            updatedCoveredEntities[coveredEntityId] = [...roles];

            data.coveredEntities = updatedCoveredEntities;

            return await this.setCoveredEntityClaims(data);
        }

        return false;
    }

    static setAffiliateClaims = async (data) => {
        if (isObjectNotEmpty(data) && isStringNotEmpty(data.userId)) {
            const func = httpsCallable(functions, "setauthuseraffiliateclaims", { timeout: 6000000 });
            const res = await func(data);

            return isObjectNotEmpty(res) && isBooleanTrue(res.data);
        }

        return false;
    }

    static updateAffiliateClaims = async (affiliateId, userId, roles = []) => {
        if (isStringNotEmpty(affiliateId) && isStringNotEmpty(userId)) {
            let data = { userId: userId };

            const existingClaims = await this.getClaims(userId);

            let updatedAffiliates = {};
            if (isObjectNotEmpty(existingClaims) && isObjectNotEmpty(existingClaims.affiliates)) {
                updatedAffiliates = { ...existingClaims.affiliates };
            }

            updatedAffiliates[affiliateId] = [...roles];

            data.affiliates = updatedAffiliates;

            return await this.setAffiliateClaims(data);
        }

        return false;
    }

    static setPartnerClaims = async (data) => {
        if (isObjectNotEmpty(data) && isStringNotEmpty(data.userId)) {
            const func = httpsCallable(functions, "setauthuserpartnerclaims", { timeout: 6000000 });
            const res = await func(data);

            return isObjectNotEmpty(res) && isBooleanTrue(res.data);
        }

        return false;
    }

    static updatePartnerClaims = async (partnerId, userId, roles = []) => {
        if (isStringNotEmpty(partnerId) && isStringNotEmpty(userId)) {
            let data = { userId: userId };

            const existingClaims = await this.getClaims(userId);

            let updatedPartners = {};
            if (isObjectNotEmpty(existingClaims) && isObjectNotEmpty(existingClaims.partners)) {
                updatedPartners = { ...existingClaims.partners };
            }

            updatedPartners[partnerId] = [...roles];
            data.partners = updatedPartners;

            return await this.setPartnerClaims(data);
        }

        return false;
    }

    static setPhoneVerificationClaim = async (data) => {
        if (isObjectNotEmpty(data) && isStringNotEmpty(data.userId)) {
            const func = httpsCallable(functions, "setauthuserphoneverifiedclaim", { timeout: 6000000 });
            const res = await func(data);

            return isObjectNotEmpty(res) && isBooleanTrue(res.data);
        }

        return false;
    }

    static updatePhoneVerificationClaim = async (userId, phone, phoneVerified = false, testMode = false) => {
        if (isStringNotEmpty(userId)) {

            let data = { userId: userId, phoneVerified: isBooleanTrue(phoneVerified), phone: isBooleanTrue(phoneVerified) && isStringNotEmpty(phone) ? toE164PhoneNumber(phone) : null, testMode: isBooleanTrue(testMode) };
            let success = await this.setPhoneVerificationClaim(data);
            if (isBooleanTrue(success)) {
                if (userId === auth.currentUser.uid) {
                    await auth.currentUser.getIdToken(true);
                }

                return true;
            }
        }

        return false;
    }

    static createUserForAuthUser = async (userId) => {
        const existingAuthUser = await this.getByUserId(userId);
        if (isObjectNotEmpty(existingAuthUser)) {
            if (isObjectNotEmpty(existingAuthUser)) {
                const email = existingAuthUser.email;

                let names = existingAuthUser.displayName.split(' ');
                let firstName = null;
                let lastName = null;
                if (isListNotEmpty(names)) {
                    firstName = names[0];
                    lastName = names[1];
                }
                const newUser = await new User({
                    userId: userId,
                    email: email,
                    firstName: firstName,
                    lastName: lastName
                }).create();
                if (isObjectNotEmpty(newUser)) {
                    return true;
                }
            }
        }

        return false;
    }

    static create = async (email, phone, firstName, lastName, signInUser = false, allowExistingUser = false) => {
        try {
            const password = AuthService.randomPasswordGenerator(16);
            const func = httpsCallable(functions, "createauthuser", { timeout: 6000000 });
            let req = {
                password: password,
                email: isStringNotEmpty(email) ? toLower(email) : null,
                phone: isStringNotEmpty(phone) ? toE164PhoneNumber(phone) : null,
                displayName: `${firstName} ${lastName}`
            };

            const res = await func(req);
            if (isObjectNotEmpty(res) && isObjectNotEmpty(res.data) && isStringNotEmpty(res.data.uid)) {
                if (isBooleanTrue(signInUser)) {
                    await AuthService.doSignInWithEmailAndPassword(email, password);
                }

                return res.data.uid;
            }
        } catch (error) {
            const errorCode = getFirebaseErrorCode(error);
            console.log(errorCode);
            if (isBooleanTrue(allowExistingUser)) {
                if (isStringNotEmpty(errorCode) && (errorCode === "auth/email-already-in-use" || errorCode === "auth/email-already-exists")) {
                    try {
                        return await this.getUserIdByEmail(toLower(email));
                    }
                    catch (err) {
                        throw err;
                    }
                }
            }

            throw error;
        }

        return null;
    }

    static createWithPassword = async (email, password, phone, firstName, lastName, signInUser = false) => {
        try {
            // console.log(moment().toISOString());
            const func = httpsCallable(functions, "createauthuser", { timeout: 6000000 });
            let req = {
                password: password,
                email: isStringNotEmpty(email) ? toLower(email) : null,
                phone: isStringNotEmpty(phone) ? toE164PhoneNumber(phone) : null,
                displayName: `${firstName} ${lastName}`
            };

            const res = await func(req);
            if (isObjectNotEmpty(res) && isObjectNotEmpty(res.data) && isStringNotEmpty(res.data.uid)) {
                // console.log('authUser', res.data);
                // console.log(moment().toISOString());
                if (isBooleanTrue(signInUser)) {
                    await AuthService.doSignInWithEmailAndPassword(email, password);
                }
                // console.log('Signed in authuser');
                // console.log(moment().toISOString());

                return res.data.uid;
            }
        } catch (error) {
            const errorCode = getFirebaseErrorCode(error);
            console.log(errorCode);
            // if (isStringNotEmpty(errorCode) && (errorCode === "auth/email-already-in-use" || errorCode === "auth/email-already-exists")) {
            //     try {
            //         const existingAuthUser = await this.getByEmail(toLower(email));
            //         if (isObjectNotEmpty(existingAuthUser) && isStringNotEmpty(existingAuthUser.uid)) {
            //             return existingAuthUser.uid;
            //         }
            //     }
            //     catch (err) {
            //         throw err;
            //     }
            // }

            throw error;
        }

        return null;
    }


    static createGuestSupportAccount = async (name, email, phone) => {
        const normalizedEmail = toLower(email.trim());
        const nameParts = name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Check if an account with this email already exists
        const exists = await AuthUser.existsByEmail(normalizedEmail);
        if (isBooleanTrue(exists)) {
            return { success: false, exists: true };
        }

        // Create auth user + User doc (signs in automatically via client SDK)
        const password = AuthService.randomPasswordGenerator(16);
        const newUser = await AuthService.doCreateUserWithEmailAndPasswordAndName(
            normalizedEmail, password, firstName, lastName, {
            email: normalizedEmail,
            phone: isStringNotEmpty(phone) ? phone.trim() : null,
            firstName,
            lastName,
            supportCategories: [],
            // the guest form is shown to a visitor signed in as no one, on their own device
            timeZone: getDeviceAccountTimeZone()
        }, ACCOUNT_CONSENT_SOURCES.SUPPORT
        );

        if (isObjectNotEmpty(newUser) && isStringNotEmpty(newUser.userId)) {
            return { success: true, userId: newUser.userId };
        }

        return { success: false };
    }

    // creates the account server-side and deliberately does NOT sign the customer in - the caller does
    // that, so the users document that isSignedIn() requires exists before any listener can attach
    //
    // returns { user, conflict } - 'PHONE' or 'EMAIL'. not retried on a transport failure, unlike the
    // records call after it: replaying a create that actually succeeded reads as a duplicate email
    // consentSource names the screen the account is created on; the server dates the marketing consent to the creation.
    // device.deviceInfo is this browser's IPQS result, for the zone the device reports
    static createAuthUserAndUserFromLead = async (lead, sendWelcomeEmailToUser = false, consentSource = null, device = {}) => {
        let userRefId = isStringNotEmpty(lead.userRefId) ? lead.userRefId : null;

        let password = lead.password;

        let email = toLower(lead.email);
        let phone = toE164PhoneNumber(lead.phone);

        let firstName = isStringNotEmpty(lead.firstName) ? lead.firstName.trim() : null;
        let middleName = isStringNotEmpty(lead.middleName) ? lead.middleName.trim() : null;
        let lastName = isStringNotEmpty(lead.lastName) ? lead.lastName.trim() : null;
        let suffix = isStringNotEmpty(lead.suffix) ? lead.suffix : null;

        let gender = isStringNotEmpty(lead.gender) ? lead.gender : null;
        let dateOfBirth = isStringNotEmpty(lead.dateOfBirth) ? lead.dateOfBirth : null;

        let height = isNumberNotEmpty(lead.height) ? lead.height : null;
        let weight = isNumberNotEmpty(lead.weight) ? lead.weight : null;
        let bmi = isNumberNotEmpty(lead.bmi) ? lead.bmi : null;

        let socialSecurityNumber = isStringNotEmpty(lead.socialSecurityNumber) ? lead.socialSecurityNumber : null;

        let shippingAddress = isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null;
        let billingAddress = isObjectNotEmpty(lead.billingAddress) ? lead.billingAddress : (isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null);

        let phoneVerified = isBooleanTrue(lead.phoneVerified) ? true : false;

        const func = httpsCallable(functions, 'createfunnelaccount', { timeout: CREATE_FUNNEL_ACCOUNT_TIMEOUT });
        const res = await func({
            email: email,
            password: password,
            phone: phone,

            profile: {
                patientId: userRefId,
                userRefId: userRefId,

                email: email,
                phone: phone,

                firstName: firstName,
                middleName: middleName,
                lastName: lastName,
                suffix: suffix,

                gender: gender,
                dateOfBirth: dateOfBirth,

                height: height,
                weight: weight,
                bmi: bmi,

                socialSecurityNumber: socialSecurityNumber,

                billingAddress: billingAddress,
                shippingAddress: shippingAddress,

                phoneVerified: phoneVerified
            },

            consentSource: consentSource,
            // the browser's own address for the signed agreements queued with the account
            ipAddress: isStringNotEmpty(lead.ipAddress) ? lead.ipAddress : null,
            timeZone: getDeviceAccountTimeZone(device?.deviceInfo ?? null)
        });

        const newUser = res?.data?.user ?? null;
        if (isObjectNotEmpty(newUser) && isStringNotEmpty(newUser?.userId)) {
            if (isBooleanTrue(sendWelcomeEmailToUser)) {
                await sendWelcomeEmail(email, firstName, newUser?.userId);
            }

            return { user: newUser, conflict: null };
        }

        return { user: null, conflict: res?.data?.conflict ?? null };
    }

    // an email that already has an account keeps that account's record - its agreements, preferences and history - and
    // gains only the roles asked for; a role it already holds is never taken away. the profile's role flags are what
    // staff see, so the login's claims are re-synced to them on every submit, repairing any drift a partial failure left
    static addRolesToExistingUser = async (userId, requestedRoles = {}) => {
        // read as it is now rather than as the form showed it, so a role removed since is never written back
        const existingUser = await User.getById(userId);
        if (isObjectEmpty(existingUser)) {
            return null;
        }

        const roles = {};
        for (const role of ROLE_FLAGS) {
            roles[role] = isBooleanTrue(existingUser[role]) || isBooleanTrue(requestedRoles[role]);
        }
        const isFriendsAndFamily = isBooleanTrue(existingUser.isFriendsAndFamily) || isBooleanTrue(requestedRoles.isFriendsAndFamily);

        const isUnchanged = ROLE_FLAGS.every((role) => roles[role] === isBooleanTrue(existingUser[role])) && isFriendsAndFamily === isBooleanTrue(existingUser.isFriendsAndFamily);
        if (isUnchanged) {
            // the phone claim is left out, so it is left as it is
            const isResynced = await this.createRoleClaims(userId, roles, undefined);
            return isBooleanTrue(isResynced) ? existingUser : null;
        }

        // every role is named, because updateFields hands them to createRoleClaims and an unnamed one would be revoked
        const isUpdated = await User.updateFields(userId, { ...roles, isFriendsAndFamily });

        return isBooleanTrue(isUpdated) ? { ...existingUser, ...roles, isFriendsAndFamily } : null;
    }

    // the profile behind an email that already has a login, with the login's own claims so the add forms can show
    // where the two have drifted, or null; the forms show it to staff and need their confirmation before adding roles.
    // loginEmail is the email the login was found by, which the profile's own email does not always match
    static getExistingUserByEmail = async (email) => {
        // most emails added are new, and this check answers them from a warm instance
        const isExisting = await this.existsByEmail(toLower(email));
        if (isBooleanFalse(isExisting)) {
            return null;
        }

        const userId = await this.getUserIdByEmail(toLower(email));
        if (isStringEmpty(userId)) {
            return null;
        }

        const [user, loginClaims] = await Promise.all([User.getById(userId), this.getClaims(userId)]);
        if (isObjectEmpty(user)) {
            return null;
        }

        return { ...user, userId: userId, loginEmail: toLower(email), loginClaims: isObjectNotEmpty(loginClaims) ? loginClaims : {} };
    }

    // roles names each role the new account gets, plus isFriendsAndFamily, so no flag can land on the wrong role; it is
    // checked before the login is created, so a call passing the flags one by one creates nothing
    static createAuthUserAndUserWithWelcomeEmail = async (email, phone, firstName, middleName, lastName, suffix, gender, race, ethnicity, dateOfBirth, streetAddress1, streetAddress2, city, state, postalCode, country, roles = {}) => {
        if (!isPlainObject(roles)) {
            throw new Error('createAuthUserAndUserWithWelcomeEmail takes the roles as one object of named flags.');
        }

        const userId = await this.create(email, null, firstName, lastName, false, true);
        if (isStringNotEmpty(userId)) {
            // create answers the existing login for an email in use. roles go onto an existing account only after staff
            // confirm it on the add form, so one that appeared since the form checked is refused, never changed
            const existingUser = await User.getById(userId);
            if (isObjectNotEmpty(existingUser)) {
                throw new Error(EXISTING_ACCOUNT_UNCONFIRMED_MESSAGE);
            }

            const newUser = await new User({
                userId: userId,
                email: toLower(email),
                phone: toE164PhoneNumber(phone),
                firstName: firstName,
                middleName: middleName,
                lastName: lastName,
                suffix: suffix,
                gender: gender,
                race: race,
                ethnicity: ethnicity,
                dateOfBirth: dateOfBirth,
                shippingAddress: new Address({
                    streetAddress1: streetAddress1,
                    streetAddress2: streetAddress2,
                    city: city,
                    state: state,
                    postalCode: postalCode,
                    country: country
                }).toDto(),
                billingAddress: new Address({
                    streetAddress1: streetAddress1,
                    streetAddress2: streetAddress2,
                    city: city,
                    state: state,
                    postalCode: postalCode,
                    country: country
                }).toDto(),
                ...toRoleFlags(roles),
                isFriendsAndFamily: isBooleanTrue(roles.isFriendsAndFamily)
            }).dateMarketingConsent(STAFF_CREATED_CONSENT_SOURCE).resolveTimeZone().create();
            if (isObjectNotEmpty(newUser)) {
                await sendWelcomeEmail(email, firstName, userId);

                // the account exists and its welcome email is sent either way; staff are told when its login did not
                // take the roles, so they save it again from the user's page rather than believe it is done
                if (isBooleanFalse(newUser.isRoleClaimsApplied)) {
                    throw new Error('The account was created and its welcome email sent, but its permissions could not be applied to the login. Open the account from the user\'s page and save it to apply them.');
                }

                return newUser;
            }
        }

        return null;
    }

    // isExistingAccountConfirmed is true only once staff confirmed, on the add form, the account the email already has
    static createAuthUserAndUserAndCoveredEntityUserWithWelcomeEmail = async (email, phone, firstName, middleName, lastName, suffix, shippingAddress, coveredEntityId, roles = [], isExistingAccountConfirmed = false) => {
        const userId = await this.create(email, null, firstName, lastName, false, true);
        if (isStringNotEmpty(userId)) {
            let existingUser = await User.getById(userId);
            if (isObjectEmpty(existingUser)) {
                existingUser = await new User({
                    userId: userId,

                    email: email,
                    phone: phone,

                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName,
                    suffix: suffix,

                    shippingAddress: shippingAddress,
                    billingAddress: shippingAddress,

                    isCoveredEntityUser: true
                }).dateMarketingConsent(STAFF_CREATED_CONSENT_SOURCE).resolveTimeZone().create();
                if (isBooleanFalse(existingUser?.isRoleClaimsApplied)) {
                    throw new Error(NEW_ACCOUNT_ROLE_NOT_APPLIED_MESSAGE);
                }
            } else {
                // a missing confirmation refuses as a false one does
                if (!isBooleanTrue(isExistingAccountConfirmed)) {
                    throw new Error(EXISTING_ACCOUNT_UNCONFIRMED_MESSAGE);
                }

                // the login's claims are written first and the profile only once they took
                existingUser = await this.addRolesToExistingUser(userId, { isCoveredEntityUser: true });
                if (isObjectEmpty(existingUser)) {
                    throw new Error(EXISTING_ACCOUNT_ROLE_NOT_ADDED_MESSAGE);
                }
            }
            if (isObjectNotEmpty(existingUser)) {
                const newCoveredEntityUser = await new CoveredEntityUser({
                    coveredEntityId: coveredEntityId,
                    userId: userId,

                    email: email,
                    phone: phone,

                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName,
                    suffix: suffix,

                    shippingAddress: shippingAddress,
                    billingAddress: shippingAddress,

                    roles: roles
                }).create();
                if (isObjectNotEmpty(newCoveredEntityUser)) {
                    await sendWelcomeEmail(email, firstName, userId);

                    return newCoveredEntityUser;
                }
            }
        }

        return null;
    }

    // isExistingAccountConfirmed is true only once staff confirmed, on the add form, the account the email already has
    static createAuthUserAndUserAndAffiliateUserWithWelcomeEmail = async (email, phone, firstName, middleName, lastName, suffix, shippingAddress, affiliateId, roles = [], isExistingAccountConfirmed = false) => {
        const userId = await this.create(email, null, firstName, lastName, false, true);
        if (isStringNotEmpty(userId)) {
            let existingUser = await User.getById(userId);
            if (isObjectEmpty(existingUser)) {
                existingUser = await new User({
                    userId: userId,

                    email: email,
                    phone: phone,

                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName,
                    suffix: suffix,

                    shippingAddress: shippingAddress,
                    billingAddress: shippingAddress,

                    isAffiliateUser: true
                }).dateMarketingConsent(STAFF_CREATED_CONSENT_SOURCE).resolveTimeZone().create();
                if (isBooleanFalse(existingUser?.isRoleClaimsApplied)) {
                    throw new Error(NEW_ACCOUNT_ROLE_NOT_APPLIED_MESSAGE);
                }
            } else {
                // a missing confirmation refuses as a false one does
                if (!isBooleanTrue(isExistingAccountConfirmed)) {
                    throw new Error(EXISTING_ACCOUNT_UNCONFIRMED_MESSAGE);
                }

                // only the role flags are written, never the rest of the profile. every role is named, which is what makes
                // updateFields write the login's claims first and revoke none of them
                const updatedRoles = { ...toRoleFlags(existingUser), isAffiliateUser: true };
                const isUpdated = await User.updateFields(userId, updatedRoles);
                if (isBooleanTrue(isUpdated)) {
                    existingUser = { ...existingUser, ...updatedRoles };
                } else {
                    throw new Error(EXISTING_ACCOUNT_ROLE_NOT_ADDED_MESSAGE);
                }
            }
            if (isObjectNotEmpty(existingUser)) {
                const newAffiliateUser = await new AffiliateUser({
                    affiliateId: affiliateId,
                    userId: userId,

                    email: email,
                    phone: phone,

                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName,
                    suffix: suffix,

                    shippingAddress: shippingAddress,
                    billingAddress: shippingAddress,

                    roles: roles
                }).create();
                if (isObjectNotEmpty(newAffiliateUser)) {
                    await sendWelcomeEmail(email, firstName, userId);

                    return newAffiliateUser;
                }
            }
        }

        return null;
    }

    // isExistingAccountConfirmed is true only once staff confirmed, on the add form, the account the email already has
    static createAuthUserAndUserAndPartnerUserWithWelcomeEmail = async (email, phone, firstName, middleName, lastName, suffix, shippingAddress, partnerId, roles = [], isExistingAccountConfirmed = false) => {
        const userId = await this.create(email, null, firstName, lastName, false, true);
        if (isStringNotEmpty(userId)) {
            let existingUser = await User.getById(userId);
            if (isObjectEmpty(existingUser)) {
                existingUser = await new User({
                    userId: userId,

                    email: email,
                    phone: phone,

                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName,
                    suffix: suffix,

                    shippingAddress: shippingAddress,
                    billingAddress: shippingAddress,

                    isPartnerUser: true
                }).dateMarketingConsent(STAFF_CREATED_CONSENT_SOURCE).resolveTimeZone().create();
                if (isBooleanFalse(existingUser?.isRoleClaimsApplied)) {
                    throw new Error(NEW_ACCOUNT_ROLE_NOT_APPLIED_MESSAGE);
                }
            } else {
                // a missing confirmation refuses as a false one does
                if (!isBooleanTrue(isExistingAccountConfirmed)) {
                    throw new Error(EXISTING_ACCOUNT_UNCONFIRMED_MESSAGE);
                }

                // only the role flags are written, never the rest of the profile. every role is named, which is what makes
                // updateFields write the login's claims first and revoke none of them
                const updatedRoles = { ...toRoleFlags(existingUser), isPartnerUser: true };
                const isUpdated = await User.updateFields(userId, updatedRoles);
                if (isBooleanTrue(isUpdated)) {
                    existingUser = { ...existingUser, ...updatedRoles };
                } else {
                    throw new Error(EXISTING_ACCOUNT_ROLE_NOT_ADDED_MESSAGE);
                }
            }
            if (isObjectNotEmpty(existingUser)) {
                const newPartnerUser = await new PartnerUser({
                    partnerId: partnerId,
                    userId: userId,

                    email: email,
                    phone: phone,

                    firstName: firstName,
                    middleName: middleName,
                    lastName: lastName,
                    suffix: suffix,

                    shippingAddress: shippingAddress,
                    billingAddress: shippingAddress,

                    roles: roles
                }).create();
                if (isObjectNotEmpty(newPartnerUser)) {
                    await sendWelcomeEmail(email, firstName, userId);

                    return newPartnerUser;
                }
            }
        }

        return null;
    }

    static update = async (userId, email, phone, firstName, lastName, photoURL) => {
        try {
            if (isStringNotEmpty(userId)) {
                const updatedAuthUserData = {
                    userId: userId
                };

                // allows updating values but not setting values to null
                if (isStringNotEmpty(email)) { updatedAuthUserData.email = toLower(email); }
                if (isStringNotEmpty(phone)) { updatedAuthUserData.phone = toE164PhoneNumber(phone); }
                if (isStringNotEmpty(firstName) && isStringNotEmpty(lastName)) { updatedAuthUserData.displayName = `${firstName} ${lastName}`; }
                if (isStringNotEmpty(photoURL)) { updatedAuthUserData.photoURL = photoURL; }

                const func = httpsCallable(functions, "updateauthuser", { timeout: 6000000 });
                const res = await func(updatedAuthUserData);
                if (isObjectNotEmpty(res) && isObjectNotEmpty(res.data)) {
                    // console.log(res.data);
                    return res.data;
                }
            }
        } catch (error) {
            console.log(error);
        }

        return null;
    }

    static removeSecondaryFactor = async (userId) => {
        try {
            if (isStringNotEmpty(userId)) {
                const updatedAuthUserData = {
                    userId: userId
                };

                const func = httpsCallable(functions, "removesecondaryfactor", { timeout: 6000000 });
                const res = await func(updatedAuthUserData);
                if (isObjectNotEmpty(res) && isObjectNotEmpty(res.data)) {
                    // console.log(res.data);
                    return res.data;
                }
            }
        } catch (error) {
            console.log(error);
        }

        return null;
    }

    static disableAccount = async (userId) => {
        try {
            if (isStringNotEmpty(userId)) {
                const updatedAuthUserData = {
                    userId: userId
                };

                const func = httpsCallable(functions, "disableaccount", { timeout: 6000000 });
                const res = await func(updatedAuthUserData);
                if (isObjectNotEmpty(res) && isObjectNotEmpty(res.data)) {
                    // console.log(res.data);
                    return res.data;
                }
            }
        } catch (error) {
            console.log(error);
        }

        return null;
    }

    static enableAccount = async (userId) => {
        try {
            if (isStringNotEmpty(userId)) {
                const updatedAuthUserData = {
                    userId: userId
                };

                const func = httpsCallable(functions, "enableaccount", { timeout: 6000000 });
                const res = await func(updatedAuthUserData);
                if (isObjectNotEmpty(res) && isObjectNotEmpty(res.data)) {
                    // console.log(res.data);
                    return res.data;
                }
            }
        } catch (error) {
            console.log(error);
        }

        return null;
    }

    static getImpersonationCustomToken = async (userId) => {
        try {
            if (isStringNotEmpty(userId)) {
                const func = httpsCallable(functions, "getauthuserimpersonationtoken", { timeout: 6000000 });
                const res = await func({ userId: userId });

                return res.data;
            }
        } catch (error) {
            console.log(error);
            throw error;
        }

        return null;
    }

    static delete = async (userId) => {
        try {
            if (isStringNotEmpty(userId)) {
                const func = httpsCallable(functions, "deleteauthuser", { timeout: 6000000 });
                const res = await func({ userId: userId });

                return isObjectNotEmpty(res) && isBooleanTrue(res.data);
            }
        } catch (error) {
            console.log(error);
        }

        return false;
    }

    static deleteMultiple = async (userIds) => {
        try {
            if (isListNotEmpty(userIds)) {
                const func = httpsCallable(functions, "deleteauthusers", { timeout: 6000000 });
                const res = await func({ userIds: userIds });

                return isObjectNotEmpty(res) && isBooleanTrue(res.data);
            }
        } catch (error) {
            console.log(error);
        }

        return false;
    }

    static getIntercomUserHash = async (userId) => {
        const func = httpsCallable(functions, "getintercomuserhash", { timeout: 6000000 });
        const res = await func({ userId: userId });
        return res.data;
    }
}

export default AuthUser;
